import { useCallback, useDeferredValue, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect } from '../lib/api';
import { ArrowLeft, CheckCircle2, Clock3, ExternalLink, RefreshCw, Search, ServerCog, Wrench, XCircle } from 'lucide-react';

const ADMIN_EMAIL = 'a.burkov@gmail.com';
const TRIGGER_CONCURRENCY = 3;
const TRIGGER_REQUESTS_PER_MINUTE = 24;
const TRIGGER_SPACING_MS = Math.ceil(60_000 / TRIGGER_REQUESTS_PER_MINUTE);
const POLL_CONCURRENCY = 4;
const POLL_INTERVAL_MS = 5000;
const INITIAL_POLL_DELAY_MS = 2500;

interface BackendOwnerRow {
  id: string;
  name: string;
  logo_emoji: string | null;
  slug: string | null;
  public_id: string | null;
  cloud_run_job_name: string | null;
  cloud_run_service_url: string | null;
  build_status: string | null;
  last_build_id: string | null;
  last_build_at: string | null;
  forked_from: string | null;
  profiles: {
    display_name: string | null;
    email: string;
  } | null;
}

interface ForkRow {
  id: string;
  name: string;
  slug: string | null;
  public_id: string | null;
  forked_from: string | null;
}

interface BackendTarget extends BackendOwnerRow {
  dependentForks: ForkRow[];
}

type LocalBuildPhase = 'queued' | 'triggering' | 'building' | 'ready' | 'failed';

interface LocalBuildState {
  phase: LocalBuildPhase;
  message: string;
  updatedAt: number;
}

function isActivePhase(phase: string | null | undefined): boolean {
  return phase === 'queued' || phase === 'triggering' || phase === 'building';
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildPublicPath(project: { public_id: string | null; slug: string | null }): string | null {
  if (!project.public_id) return null;
  return `/p/${project.public_id}${project.slug ? `/${project.slug}` : ''}`;
}

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;

  let index = 0;
  async function next(): Promise<void> {
    while (index < items.length) {
      const currentIndex = index;
      index += 1;
      await worker(items[currentIndex]);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () => next());
  await Promise.all(workers);
}

function normalizeProfile<T extends { profiles: unknown }>(row: T): T & { profiles: BackendOwnerRow['profiles'] } {
  return {
    ...row,
    profiles: Array.isArray((row as { profiles: unknown[] }).profiles)
      ? (((row as { profiles: unknown[] }).profiles[0] as BackendOwnerRow['profiles']) || null)
      : ((row as { profiles: BackendOwnerRow['profiles'] }).profiles || null),
  };
}

function buildStatusTone(phase: string): string {
  switch (phase) {
    case 'ready':
      return 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400';
    case 'failed':
      return 'border-red-500/20 bg-red-500/10 text-red-400';
    case 'building':
    case 'triggering':
    case 'queued':
      return 'border-amber-500/20 bg-amber-500/10 text-amber-300';
    default:
      return 'border-theme-border-muted bg-theme-surface text-theme-text-muted';
  }
}

function getEffectivePhase(target: BackendTarget, localState?: LocalBuildState): string {
  return localState?.phase || target.build_status || 'idle';
}

export function AdminBackendRebuildsPage() {
  const { user, session, loading: authLoading, ensureFreshToken } = useAuth();
  const [targets, setTargets] = useState<BackendTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dispatchingAll, setDispatchingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [buildStates, setBuildStates] = useState<Map<string, LocalBuildState>>(new Map());
  const pollInFlightRef = useRef(false);
  const nextTriggerSlotRef = useRef(0);

  useEffect(() => {
    document.title = 'Backend Rebuilds - Llambada';
    return () => {
      document.title = 'Llambada';
    };
  }, []);

  const updateBuildState = useCallback((projectId: string, nextState: LocalBuildState | null) => {
    setBuildStates((prev) => {
      const next = new Map(prev);
      if (nextState) {
        next.set(projectId, nextState);
      } else {
        next.delete(projectId);
      }
      return next;
    });
  }, []);

  const fetchTargets = useCallback(async (options?: { silent?: boolean }) => {
    if (!session) return;

    if (options?.silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const [ownersResult, forksResult] = await Promise.all([
        dbSelect<BackendOwnerRow[]>('projects', {
          token: session.access_token,
          select: 'id,name,logo_emoji,slug,public_id,cloud_run_job_name,cloud_run_service_url,build_status,last_build_id,last_build_at,forked_from,profiles!projects_user_id_fkey(display_name,email)',
          filters: {
            cloud_run_job_name: 'not.is.null',
            forked_from: 'is.null',
            is_published: 'eq.true',
          },
          order: 'name.asc',
        }),
        dbSelect<ForkRow[]>('projects', {
          token: session.access_token,
          select: 'id,name,slug,public_id,forked_from',
          filters: { forked_from: 'not.is.null', is_published: 'eq.true' },
          order: 'name.asc',
        }),
      ]);

      if (ownersResult.error) throw ownersResult.error;
      if (forksResult.error) throw forksResult.error;

      const dependentForksByRoot = new Map<string, ForkRow[]>();
      for (const fork of forksResult.data || []) {
        if (!fork.forked_from) continue;
        const siblings = dependentForksByRoot.get(fork.forked_from) || [];
        siblings.push(fork);
        dependentForksByRoot.set(fork.forked_from, siblings);
      }

      const normalizedTargets = (ownersResult.data || []).map((row) => {
        const owner = normalizeProfile(row);
        return {
          ...owner,
          dependentForks: dependentForksByRoot.get(owner.id) || [],
        } satisfies BackendTarget;
      });

      setTargets(normalizedTargets);
      setBuildStates((prev) => {
        const next = new Map<string, LocalBuildState>();

        for (const target of normalizedTargets) {
          const current = prev.get(target.id);
          if (!current) continue;

          if (target.build_status === 'ready') {
            next.set(target.id, {
              phase: 'ready',
              message: 'Build completed successfully',
              updatedAt: current.updatedAt,
            });
            continue;
          }

          if (target.build_status === 'failed') {
            next.set(target.id, {
              phase: 'failed',
              message: 'Build failed',
              updatedAt: current.updatedAt,
            });
            continue;
          }

          next.set(target.id, current);
        }

        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rebuild targets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchTargets();
    }
  }, [user, fetchTargets]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!session) return null;
    return await ensureFreshToken({ forceRefresh: false }) || session.access_token;
  }, [ensureFreshToken, session]);

  const waitForTriggerSlot = useCallback(async (): Promise<void> => {
    const now = Date.now();
    const scheduledAt = Math.max(now, nextTriggerSlotRef.current);
    nextTriggerSlotRef.current = scheduledAt + TRIGGER_SPACING_MS;

    if (scheduledAt > now) {
      await new Promise((resolve) => window.setTimeout(resolve, scheduledAt - now));
    }
  }, []);

  const triggerRebuild = useCallback(async (projectId: string) => {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    updateBuildState(projectId, {
      phase: 'queued',
      message: 'Waiting for Cloud Build quota window...',
      updatedAt: Date.now(),
    });

    await waitForTriggerSlot();

    updateBuildState(projectId, {
      phase: 'triggering',
      message: 'Triggering rebuild...',
      updatedAt: Date.now(),
    });

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/trigger-project-build`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectId, force: true }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || `Rebuild failed (${response.status})`);
    }

    setTargets((prev) => prev.map((target) => (
      target.id === projectId
        ? {
          ...target,
          build_status: 'building',
          last_build_id: typeof data.buildId === 'string' ? data.buildId : target.last_build_id,
        }
        : target
    )));

    updateBuildState(projectId, {
      phase: 'building',
      message: data.message || 'Build started',
      updatedAt: Date.now(),
    });
  }, [getAccessToken, updateBuildState, waitForTriggerSlot]);

  const handleRebuildOne = useCallback(async (projectId: string) => {
    try {
      await triggerRebuild(projectId);
    } catch (err) {
      updateBuildState(projectId, {
        phase: 'failed',
        message: err instanceof Error ? err.message : 'Rebuild failed',
        updatedAt: Date.now(),
      });
    }
  }, [triggerRebuild, updateBuildState]);

  const visibleTargets = targets.filter((target) => {
    const query = deferredSearch.trim().toLowerCase();
    if (!query) return true;

    const haystack = [
      target.name,
      target.cloud_run_job_name || '',
      target.profiles?.display_name || '',
      target.profiles?.email || '',
      ...target.dependentForks.map((fork) => fork.name),
    ].join(' ').toLowerCase();

    return haystack.includes(query);
  });

  const failedTargetIds = visibleTargets
    .filter((target) => {
      const local = buildStates.get(target.id);
      return getEffectivePhase(target, local) === 'failed';
    })
    .map((target) => target.id);

  const buildingIds = targets
    .filter((target) => {
      const local = buildStates.get(target.id);
      if (local && isActivePhase(local.phase)) {
        return true;
      }
      return target.build_status === 'building';
    })
    .map((target) => target.id);

  const dispatchRebuildBatch = useCallback(async (projectIds: string[]) => {
    if (projectIds.length === 0) return;

    projectIds.forEach((projectId) => {
      updateBuildState(projectId, {
        phase: 'queued',
        message: 'Queued for rebuild dispatch',
        updatedAt: Date.now(),
      });
    });

    await runWithConcurrency(projectIds, TRIGGER_CONCURRENCY, async (projectId) => {
      try {
        await triggerRebuild(projectId);
      } catch (err) {
        updateBuildState(projectId, {
          phase: 'failed',
          message: err instanceof Error ? err.message : 'Rebuild failed',
          updatedAt: Date.now(),
        });
      }
    });
  }, [triggerRebuild, updateBuildState]);

  useEffect(() => {
    if (!session || buildingIds.length === 0) return;

    let cancelled = false;

    const pollStatuses = async () => {
      if (cancelled || pollInFlightRef.current) return;
      pollInFlightRef.current = true;

      try {
        await runWithConcurrency(buildingIds, POLL_CONCURRENCY, async (projectId) => {
          const accessToken = await getAccessToken();
          if (!accessToken) return;

          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const response = await fetch(
            `${supabaseUrl}/functions/v1/check-build-status?projectId=${projectId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );

          if (!response.ok) return;
          const data = await response.json();

          if (cancelled) return;

          if (data.buildStatus === 'ready') {
            setTargets((prev) => prev.map((target) => (
              target.id === projectId
                ? {
                  ...target,
                  build_status: 'ready',
                  cloud_run_service_url: data.serviceUrl || target.cloud_run_service_url,
                  last_build_at: new Date().toISOString(),
                }
                : target
            )));
            updateBuildState(projectId, {
              phase: 'ready',
              message: data.message || 'Build completed successfully',
              updatedAt: Date.now(),
            });
          } else if (data.buildStatus === 'failed') {
            setTargets((prev) => prev.map((target) => (
              target.id === projectId
                ? {
                  ...target,
                  build_status: 'failed',
                  last_build_at: new Date().toISOString(),
                }
                : target
            )));
            updateBuildState(projectId, {
              phase: 'failed',
              message: data.message || 'Build failed',
              updatedAt: Date.now(),
            });
          } else {
            updateBuildState(projectId, {
              phase: 'building',
              message: data.message || 'Build in progress',
              updatedAt: Date.now(),
            });
          }
        });
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const initialTimer = window.setTimeout(pollStatuses, INITIAL_POLL_DELAY_MS);
    const interval = window.setInterval(pollStatuses, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
    };
  }, [buildingIds, getAccessToken, session, updateBuildState]);

  const handleRebuildAll = useCallback(async () => {
    if (dispatchingAll || visibleTargets.length === 0) return;

    setDispatchingAll(true);
    try {
      await dispatchRebuildBatch(visibleTargets.map((target) => target.id));
    } finally {
      setDispatchingAll(false);
    }
  }, [dispatchRebuildBatch, dispatchingAll, visibleTargets]);

  const handleRebuildFailed = useCallback(async () => {
    if (dispatchingAll || failedTargetIds.length === 0) return;

    setDispatchingAll(true);
    try {
      await dispatchRebuildBatch(failedTargetIds);
    } finally {
      setDispatchingAll(false);
    }
  }, [dispatchRebuildBatch, dispatchingAll, failedTargetIds]);

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  const totalDependents = targets.reduce((sum, target) => sum + target.dependentForks.length, 0);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
              title="Back to Admin"
            >
              <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
            </Link>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-400">
              <ServerCog className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-theme-text">Backend Rebuilds</h1>
              <p className="text-theme-text-secondary text-sm">
                Rebuild every backend-owning mini-app after `cloud-run-service` changes. Forks are counted as dependents because they reuse the parent service.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fetchTargets({ silent: true })} className="btn btn-secondary" disabled={refreshing || loading}>
              {refreshing ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <button onClick={handleRebuildFailed} className="btn btn-secondary" disabled={dispatchingAll || loading || failedTargetIds.length === 0}>
              {dispatchingAll ? <LoadingSpinner size="sm" /> : <XCircle className="h-4 w-4" />}
              Rebuild Failed Services
            </button>
            <button onClick={handleRebuildAll} className="btn btn-primary" disabled={dispatchingAll || loading || visibleTargets.length === 0}>
              {dispatchingAll ? <LoadingSpinner size="sm" /> : <Wrench className="h-4 w-4" />}
              Rebuild {deferredSearch.trim() ? 'Visible' : 'All'} Services
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Backend Services</div>
            <div className="text-2xl font-bold text-theme-text">{targets.length}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Dependent Forks</div>
            <div className="text-2xl font-bold text-theme-text">{totalDependents}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Currently Building</div>
            <div className="text-2xl font-bold text-theme-text">{buildingIds.length}</div>
          </div>
        </div>

          <div className="card p-4 mb-6">
          <div className="mb-3 text-sm text-theme-text-secondary">
            Bulk rebuilds are paced to {TRIGGER_REQUESTS_PER_MINUTE} Cloud Build create requests per minute so they stay under the project quota.
          </div>
          <label htmlFor="backendRebuildSearch" className="block text-sm font-medium text-theme-text-secondary mb-2">
            Search services, owners, or dependent forks
          </label>
          <div className="relative">
            <Search className="h-4 w-4 text-theme-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="backendRebuildSearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by project name, service name, owner, or fork..."
              className="input pl-10"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="card p-10 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : visibleTargets.length === 0 ? (
          <div className="card p-10 text-center text-theme-text-muted">
            {targets.length === 0 ? 'No backend-owning mini-apps found.' : 'No services match this search.'}
          </div>
        ) : (
          <div className="grid gap-4">
            {visibleTargets.map((target) => {
              const local = buildStates.get(target.id);
              const phase = getEffectivePhase(target, local);
              const isBusy = isActivePhase(local?.phase) || target.build_status === 'building';
              const message = local?.message || (
                target.build_status === 'building'
                  ? 'Build in progress'
                  : target.build_status === 'failed'
                  ? 'Last build failed'
                  : target.build_status === 'ready'
                  ? 'Ready'
                  : 'Idle'
              );
              const publicPath = buildPublicPath(target);

              return (
                <div key={target.id} className="card p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-3">
                        {target.logo_emoji && (
                          <div className="text-2xl leading-none mt-0.5">{target.logo_emoji}</div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-lg font-semibold text-theme-text truncate">{target.name}</h2>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${buildStatusTone(phase)}`}>
                              {phase === 'ready' && <CheckCircle2 className="h-3.5 w-3.5" />}
                              {(phase === 'building' || phase === 'triggering' || phase === 'queued') && <Clock3 className="h-3.5 w-3.5" />}
                              {phase === 'failed' && <XCircle className="h-3.5 w-3.5" />}
                              {phase}
                            </span>
                            <span className="inline-flex rounded-full border border-theme-border-muted px-2.5 py-1 text-xs font-medium text-theme-text-secondary">
                              {target.dependentForks.length} dependent {target.dependentForks.length === 1 ? 'fork' : 'forks'}
                            </span>
                          </div>

                          <div className="mt-1 text-sm text-theme-text-secondary">
                            {target.profiles?.display_name || target.profiles?.email || 'Unknown owner'}
                          </div>

                          <div className="mt-3 grid gap-2 text-sm text-theme-text-secondary md:grid-cols-2">
                            <div>
                              <span className="text-theme-text-muted">Service:</span>{' '}
                              <code className="text-theme-text">{target.cloud_run_job_name}</code>
                              {target.cloud_run_service_url && (
                                <>
                                  <br />
                                  <span className="text-theme-text-muted">URL:</span>{' '}
                                  <code className="text-theme-text text-xs break-all">{target.cloud_run_service_url}</code>
                                </>
                              )}
                            </div>
                            <div>
                              <span className="text-theme-text-muted">Last build:</span>{' '}
                              <span className="text-theme-text">{formatDate(target.last_build_at)}</span>
                            </div>
                            <div className="md:col-span-2">
                              <span className="text-theme-text-muted">Status:</span>{' '}
                              <span className="text-theme-text">{message}</span>
                            </div>
                          </div>

                          {target.dependentForks.length > 0 && (
                            <div className="mt-4 rounded-xl border border-theme-border-muted bg-theme-surface/60 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-theme-text-muted mb-2">
                                Rebuild impact
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {target.dependentForks.slice(0, 6).map((fork) => {
                                  const forkPublicPath = buildPublicPath(fork);
                                  return forkPublicPath ? (
                                    <Link
                                      key={fork.id}
                                      to={forkPublicPath}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 rounded-full border border-theme-border-muted px-2.5 py-1 text-xs text-theme-text-secondary hover:text-theme-text"
                                    >
                                      {fork.name}
                                      <ExternalLink className="h-3 w-3" />
                                    </Link>
                                  ) : (
                                    <span
                                      key={fork.id}
                                      className="inline-flex items-center rounded-full border border-theme-border-muted px-2.5 py-1 text-xs text-theme-text-secondary"
                                    >
                                      {fork.name}
                                    </span>
                                  );
                                })}
                                {target.dependentForks.length > 6 && (
                                  <span className="inline-flex items-center rounded-full border border-theme-border-muted px-2.5 py-1 text-xs text-theme-text-muted">
                                    +{target.dependentForks.length - 6} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap gap-2 xl:justify-end">
                      {publicPath && (
                        <Link to={publicPath} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                          Public Page
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      )}
                      <Link to={`/project/${target.id}`} className="btn btn-secondary">
                        Open Project
                      </Link>
                      <button
                        onClick={() => handleRebuildOne(target.id)}
                        className="btn btn-primary"
                        disabled={isBusy}
                      >
                        {isBusy ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
                        {isBusy ? 'Building...' : 'Rebuild'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
